import { useEffect, useState } from 'react';
import { BrandMark } from '../components/BrandMark';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { slugify } from '../hooks';
import { clearAll } from '../queryCache';
import { ErrorNotice, Field, PasswordInput, ThemeToggle } from '../components/ui';
import { Input } from '../components/primitives';

/**
 * AuthPage gates the authenticated app: nothing under /app renders until
 * this reports success, then it navigates there itself. It never contacts
 * an external identity provider — only this API's own password sign-in.
 */
// A same-origin, in-app path only: rejects an absolute URL, a
// protocol-relative one ("//evil.example"), and the "/\evil.example" form
// some browsers also resolve as scheme-relative — never redirect a
// successful sign-in anywhere an open-redirect could send it.
// Exported for its own focused test (see AuthPage.test.tsx) — this is the
// one piece of this file with real security consequences if it regresses.
export function safeNextPath(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) {
    return '/app';
  }
  return raw;
}

export function AuthPage() {
  // The sign-in page means no session: nothing cached by an earlier one
  // (signed out elsewhere, expired) may be shown to whoever signs in next.
  useEffect(() => clearAll(), []);
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'sign-in' | 'sign-up'>(searchParams.get('mode') === 'sign-up' ? 'sign-up' : 'sign-in');
  const navigate = useNavigate();
  // Set once per mount from the URL AppGate redirected here with — not
  // re-read after a mode switch, so it can't change under a half-filled
  // form.
  const [next] = useState(() => safeNextPath(searchParams.get('next')));
  const onSuccess = () => navigate(next, { replace: true });

  return (
    <div className="auth-shell">
      <div className="auth-shell__theme">
        <ThemeToggle />
      </div>
      <div className="auth-card">
        <Link to="/" className="auth-card__brand">
          <BrandMark size={26} />
          Commerce Ops
        </Link>
        {mode === 'sign-in' ? (
          <SignInForm onSuccess={onSuccess} onSwitchMode={() => setMode('sign-up')} />
        ) : (
          <SignUpForm onSuccess={onSuccess} onSwitchMode={() => setMode('sign-in')} />
        )}
      </div>
    </div>
  );
}

function SignInForm({ onSuccess, onSwitchMode }: { onSuccess: () => void; onSwitchMode: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.login({ email: email.trim(), password });
      onSuccess();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="form" onSubmit={handleSubmit}>
      <h1>Sign in</h1>
      <Field label="Email">
        <Input
          required
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>
      <Field label="Password">
        <PasswordInput
          required
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>
      <p className="muted auth-form__forgot-password">
        <Link to="/forgot-password">Forgot your password?</Link>
      </p>
      <ErrorNotice error={error} />
      <button className="button" type="submit" disabled={submitting || !email.trim() || !password}>
        {submitting ? 'Signing in' : 'Sign in'}
      </button>
      <p className="muted">
        No account yet?{' '}
        <button className="link-button" type="button" onClick={onSwitchMode}>
          Create your workspace
        </button>
      </p>
    </form>
  );
}

function SignUpForm({ onSuccess, onSwitchMode }: { onSuccess: () => void; onSwitchMode: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [organizationSlug, setOrganizationSlug] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.register({
        name: name.trim(),
        email: email.trim(),
        password,
        organization_name: organizationName.trim(),
        organization_slug: organizationSlug.trim() || slugify(organizationName),
      });
      onSuccess();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="form" onSubmit={handleSubmit}>
      <h1>Create your workspace</h1>
      <Field label="Your name">
        <Input required autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} />
      </Field>
      <Field label="Email">
        <Input
          required
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>
      <Field label="Password" hint="At least 10 characters.">
        <PasswordInput
          required
          minLength={10}
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>
      <Field label="Organization name">
        <Input
          required
          value={organizationName}
          onChange={(event) => setOrganizationName(event.target.value)}
          placeholder="Your agency"
        />
      </Field>
      <Field label="Organization slug">
        <Input
          value={organizationSlug}
          onChange={(event) => setOrganizationSlug(event.target.value)}
          placeholder={slugify(organizationName) || 'your-agency'}
        />
      </Field>
      <ErrorNotice error={error} />
      <button
        className="button"
        type="submit"
        disabled={submitting || !name.trim() || !email.trim() || password.length < 10 || !organizationName.trim()}
      >
        {submitting ? 'Creating' : 'Create workspace'}
      </button>
      <p className="muted">
        Already have an account?{' '}
        <button className="link-button" type="button" onClick={onSwitchMode}>
          Sign in
        </button>
      </p>
    </form>
  );
}
